import { useEffect, useRef } from "react";

import type { GuestMenuVersion } from "@dashboard/hooks/useGuestMenuVersion";

import { trackDashboardEvent } from "../../mixpanel/mixpanel";

export const SWITCH_BACK_REASONS = [
  { value: "looks_wrong", label: "Something looks wrong" },
  { value: "confusing_for_guests", label: "Guests found it confusing" },
  {
    value: "allergen_info_different",
    label: "Allergen information looks different",
  },
  { value: "missing_feature", label: "It's missing something we need" },
  { value: "prefer_classic", label: "We prefer Classic" },
  { value: "other", label: "Something else" },
] as const;

export type SwitchBackReason = (typeof SWITCH_BACK_REASONS)[number]["value"];

export const REASON_DETAIL_MAX = 280;

export type VersionChangeSource = "settings" | "preview_sheet" | "rollout";

export const trackGuestMenuVersionChanged = ({
  from,
  to,
  source,
  reason,
  detail,
}: {
  from: GuestMenuVersion;
  to: GuestMenuVersion;
  source: VersionChangeSource;
  reason?: SwitchBackReason | null;
  detail?: string;
}) => {
  const trimmed = detail?.trim() ?? "";
  trackDashboardEvent("Guest Menu Version Changed", {
    from,
    to,
    source,
    ...(to === "classic" ? { reason: reason ?? "none" } : {}),
    ...(reason === "other" && trimmed
      ? { reason_detail: trimmed.slice(0, REASON_DETAIL_MAX) }
      : {}),
  });
};

export const trackSwitchBackCancelled = (reason: SwitchBackReason | null) =>
  trackDashboardEvent("Guest Menu Switch Back Cancelled", {
    reason: reason ?? "none",
  });

export type PreviewSource =
  | "settings"
  | "themes_tip"
  | "themes_switch"
  | "themes_preview"
  | "header"
  | "menus_notice"
  | "live_callout";

export const trackGuestMenuPreviewed = (source: PreviewSource) =>
  trackDashboardEvent("Guest Menu Previewed", { source });

export type GuestMenuTip = "themes_tip" | "menus_notice" | "live_callout";

export const trackGuestMenuTipShown = (tip: GuestMenuTip) =>
  trackDashboardEvent("Guest Menu Tip Shown", { tip });

/** Sends Tip Shown the first time the tip appears while mounted. */
export const useTrackTipShown = (tip: GuestMenuTip, shown: boolean) => {
  const sentRef = useRef(false);
  useEffect(() => {
    if (!shown || sentRef.current) return;
    sentRef.current = true;
    trackGuestMenuTipShown(tip);
  }, [shown, tip]);
};

export const trackGuestMenuTipDismissed = (
  tip: GuestMenuTip,
  keptClassic?: { reason: SwitchBackReason | null; detail?: string }
) => {
  const trimmed = keptClassic?.detail?.trim() ?? "";
  trackDashboardEvent("Guest Menu Tip Dismissed", {
    tip,
    action: keptClassic ? "keep_classic" : "close",
    ...(keptClassic ? { reason: keptClassic.reason ?? "none" } : {}),
    ...(keptClassic?.reason === "other" && trimmed
      ? { reason_detail: trimmed.slice(0, REASON_DETAIL_MAX) }
      : {}),
  });
};
